import React, { useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Divider,
  Stack,
  Box,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFeatures } from "../../../../../../../../context/PermissionsAndFeatures/FeaturesContext";

const PaymentDialog = ({
  open,
  onClose,
  payments,
  setPayments,
  banks,
  totalAmount,
  onSubmit,
  isDark,
}) => {
  // استخلاص حالة الميزة multi_payment و payment_methods
  const { hasFeature, featuresLoading } = useFeatures();
  const multiPaymentEnabled = !featuresLoading && hasFeature("multi_payment");
  const paymentMethodsEnabled = !featuresLoading && hasFeature("payment_methods");

  // سنعرض جميع الطرق دائماً، لكن سنعطّل الاختيار للطرق غير المسموح بها إذا كانت الميزة معطلة
  const allMethods = ["cash", "visa", "bank"];

  // ✅ useRef للتركيز على حقل المبلغ
  const amountInputRef = useRef(null);

  // NEW: responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ✅ useEffect لتحميل الخزنة المحفوظة من localStorage عند فتح الـ Dialog
  useEffect(() => {
    if (open && payments.length > 0) {
      const savedBankId = localStorage.getItem("selected_payment_bank");
      if (savedBankId && banks.length > 0) {
        // تحديث جميع صفوف الدفع بالخزنة المحفوظة
        const updated = payments.map((payment) => ({
          ...payment,
          bank_id: savedBankId,
        }));
        setPayments(updated);
      }
      
      // ✅ التركيز على حقل المبلغ بعد تحميل البيانات
      setTimeout(() => {
        if (amountInputRef.current) {
          amountInputRef.current.focus();
          amountInputRef.current.select();
        }
      }, 100);
    }
  }, [open, banks]);

  // ✅ useEffect لإضافة اختصار Enter لتأكيد الدفع
  useEffect(() => {
    const handleKeyDown = (e) => {
      // التحقق من مفتاح Enter
      if (e.key === 'Enter' && open) {
        e.preventDefault(); // منع السلوك الافتراضي
        onSubmit(); // تنفيذ تأكيد الدفع
      }
    };

    // إضافة المستمع فقط إذا كان الـ Dialog مفتوحاً
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    }

    // تنظيف المستمع عند unmount أو إغلاق الـ Dialog
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onSubmit]);

  // Paper styling: bottom-sheet on mobile, boxed on desktop
  const paperSx = isMobile
    ? {
        margin: 0,
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: "16px 16px 0 0",
        maxHeight: "78vh",
        overflow: "auto",
      }
    : {
        borderRadius: 4,
      };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          ...paperSx,
          bgcolor: isDark ? "#232334" : "#fff",
          color: isDark ? "#fff" : "#222",
          boxShadow: 8,
          p: 0,
        },
      }}
      // center on desktop, bottom-sheet on mobile via PaperProps
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: isDark ? "#232334" : "#fbc2eb",
          color: isDark ? "#fff" : "#232334",
          fontWeight: 700,
          fontSize: 20,
          borderTopLeftRadius: isMobile ? 16 : 4,
          borderTopRightRadius: isMobile ? 16 : 4,
          pb: 1,
          pt: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span role="img" aria-label="pay" style={{ fontSize: 22 }}>
            💳
          </span>
          الدفع المتعدد
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: isDark ? "#fff" : "#232334" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          bgcolor: isDark ? "#232334" : "#fff",
          px: { xs: 2, sm: 3 },
          pt: 2,
          pb: isMobile ? 2 : 1,
        }}
      >
        <Typography
          sx={{ mb: 2, fontWeight: 500, color: "#888", textAlign: "center", fontSize: isMobile ? 14 : 15 }}
        >
          يمكنك تقسيم المبلغ على أكثر من طريقة دفع (كاش، فيزا، بنكي...)
        </Typography>

        <Stack spacing={2}>
          {payments.map((payment, idx) => (
            <Stack
              key={idx}
              direction={isMobile ? "column" : "row"}
              spacing={1}
              alignItems="center"
              sx={{
                bgcolor: isDark ? "#29293a" : "#f5f5f5",
                borderRadius: 2,
                p: 1,
                boxShadow: 1,
              }}
            >
              <TextField
                select
                label="طريقة الدفع"
                value={payment.payment_method}
                onChange={(e) => {
                  const updated = [...payments];
                  updated[idx].payment_method = e.target.value;
                  setPayments(updated);
                  localStorage.setItem(
                    "invoice_payments",
                    JSON.stringify(updated)
                  );
                }}
                sx={{ minWidth: isMobile ? "100%" : 130 }}
                size={isMobile ? "medium" : "small"}
              >
                {allMethods.map((m) => (
                  <MenuItem
                    key={m}
                    value={m}
                    disabled={!paymentMethodsEnabled && m !== "cash"}
                  >
                    {m === "cash" ? "كاش" : m === "visa" ? "فيزا" : "بنكي"}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="الخزنة"
                value={payment.bank_id}
                onChange={(e) => {
                  const updated = [...payments];
                  updated[idx].bank_id = e.target.value;
                  setPayments(updated);
                  localStorage.setItem("selected_payment_bank", e.target.value);
                  localStorage.setItem(
                    "invoice_payments",
                    JSON.stringify(updated)
                  );
                }}
                sx={{ minWidth: isMobile ? "100%" : 160 }}
                size={isMobile ? "medium" : "small"}
              >
                {banks.map((bank) => (
                  <MenuItem key={bank.id} value={bank.id}>
                    {bank.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                inputRef={idx === 0 ? amountInputRef : null}
                label="المبلغ"
                type="number"
                value={payment.amount}
                onChange={(e) => {
                  const updated = [...payments];
                  updated[idx].amount = e.target.value;
                  setPayments(updated);
                  localStorage.setItem(
                    "invoice_payments",
                    JSON.stringify(updated)
                  );
                }}
                sx={{ minWidth: isMobile ? "100%" : 120 }}
                size={isMobile ? "medium" : "small"}
                inputProps={{ min: 0 }}
                autoFocus={idx === 0}
              />

              {payments.length > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", width: isMobile ? "100%" : "auto" }}>
                  <Tooltip title="حذف طريقة الدفع">
                    <IconButton
                      color="error"
                      onClick={() => {
                        const updated = payments.filter((_, i) => i !== idx);
                        setPayments(updated);
                        localStorage.setItem(
                          "invoice_payments",
                          JSON.stringify(updated)
                        );
                      }}
                      size={isMobile ? "medium" : "small"}
                    >
                      <CloseIcon fontSize={isMobile ? "medium" : "small"} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Stack>
          ))}

          <Tooltip title={multiPaymentEnabled ? "" : "ميزة الدفع المتعدد (multi_payment) غير مفعّلة"}>
            <span>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  if (!multiPaymentEnabled) return;
                  setPayments([
                    ...payments,
                    { bank_id: "", amount: "", payment_method: "cash" },
                  ]);
                }}
                disabled={!multiPaymentEnabled}
                sx={{
                  alignSelf: "flex-end",
                  mt: 1,
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                }}
                size={isMobile ? "medium" : "small"}
              >
                إضافة طريقة دفع
              </Button>
            </span>
          </Tooltip>
        </Stack>

        <Divider sx={{ my: 2, bgcolor: isDark ? "#444" : "#eee" }} />
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontWeight: 700, color: "#888" }}>إجمالي الفاتورة:</Typography>
            <Typography sx={{ fontWeight: 700, color: "#f48fb1", fontSize: 18 }}>{totalAmount || 0}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontWeight: 700, color: "#1976d2" }}>المدفوع:</Typography>
            <Typography sx={{ fontWeight: 700, color: "#1976d2", fontSize: 18 }}>
              {payments.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0).toFixed(2)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontWeight: 700, color: "#d32f2f" }}>المتبقي:</Typography>
            <Typography sx={{ fontWeight: 700, color: "#d32f2f", fontSize: 18 }}>
              {(parseFloat(totalAmount || 0) - payments.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0)).toFixed(2)}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>

      {/* Actions: vertical full-width confirm on mobile, inline on desktop */}
      {isMobile ? (
        <Box sx={{ px: 2, pb: 2, pt: 1 }}>
          <Button onClick={onClose} color="secondary" fullWidth sx={{ mb: 1 }}>
            إلغاء
          </Button>
          <Button
            onClick={onSubmit}
            variant="contained"
            color="primary"
            fullWidth
            sx={{
              fontWeight: "bold",
              bgcolor: "#1976d2",
              color: "#fff",
              borderRadius: 2,
              py: 1.1,
              boxShadow: 2,
              fontSize: 17,
              "&:hover": { bgcolor: "#1565c0" },
            }}
            title="تأكيد الدفع (Enter)"
          >
            تأكيد الدفع
          </Button>
        </Box>
      ) : (
        <DialogActions
          sx={{
            bgcolor: isDark ? "#232334" : "#fbc2eb",
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
            px: 3,
            py: 2,
            justifyContent: "flex-end",
          }}
        >
          <Button onClick={onClose} color="secondary" sx={{ fontWeight: 600 }}>
            إلغاء
          </Button>
          <Button
            onClick={onSubmit}
            variant="contained"
            color="primary"
            sx={{
              fontWeight: "bold",
              bgcolor: "#1976d2",
              color: "#fff",
              borderRadius: 2,
              px: 4,
              ml: 2,
              boxShadow: 2,
              fontSize: 17,
              "&:hover": { bgcolor: "#1565c0" },
            }}
            title="تأكيد الدفع (Enter)"
          >
            تأكيد الدفع
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PaymentDialog;

